/**
 * VulnerabilitiesTab Component
 *
 * Wrapper component for displaying asset vulnerabilities in the AssetDrawer.
 * Renders the Vulnerabilities table with predefined filters for the specific asset.
 *
 * For Application-class assets, scopes risks to every member of the rollup
 * via the durable `(:Application)-[:HAS_MEMBER]->(:Asset)` edge resolved by
 * `applicationRollupKey` — so member-attached risks are never silently excluded.
 * See rollup-frontend-plan.md item D.
 */

import { applicationRollupKey } from '@/sections/asset/utils';
import { Vulnerabilities } from '@/sections/vulnerabilities';
import type { Asset } from '@/types';

enum TableName {
  VULNERABILITIES = 'assetDrawerVulnerabilities',
}

interface VulnerabilitiesTabProps {
  /** The asset key to filter vulnerabilities by (default for non-Applications). */
  assetKey: string;
  /**
   * The drawer's asset. For Applications (or members resolving up to one),
   * risks are scoped to the parent :Application's members via HAS_MEMBER.
   * For everything else the per-asset member-key path is used unchanged.
   */
  asset?: Asset;
}

export function VulnerabilitiesTab({
  assetKey,
  asset,
}: VulnerabilitiesTabProps) {
  // The parent :Application key when this drawer is (or rolls up to) an
  // Application; undefined otherwise. Uses applicationRollupKey.
  const appKey = asset ? applicationRollupKey(asset) : undefined;
  // Application: scope to all members via the HAS_MEMBER edge so the tab
  // aggregates across the rollup. Otherwise: filter by member key.
  const parentFilters = appKey ? { applicationKey: appKey } : { assetKey };

  return (
    <div className="size-full">
      <Vulnerabilities
        name={TableName.VULNERABILITIES}
        parentFilters={parentFilters}
        hideTitle
        inDrawer={true}
        drawerFilters={['status', 'severity', 'origin']}
      />
    </div>
  );
}
